import { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { openCalendly } from "../lib/calendly";

const PORTRAIT_PLACEHOLDER =
    "https://customer-assets.emergentagent.com/job_finance-leadership-3/artifacts/7xqmqx3j_Kumen%20Sitting%20Photo.jpg";

export default function About() {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (
        <main data-testid="about-page" className="relative bg-[#050508]">
            <Navigation />

            {/* Hero */}
            <section
                data-testid="about-hero"
                className="relative pt-40 pb-24 md:pt-48 md:pb-32 px-6 md:px-12 lg:px-20 hero-vignette grain overflow-hidden"
            >
                <div
                    aria-hidden="true"
                    className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(91,58,120,0.4) 0%, transparent 70%)",
                        filter: "blur(50px)",
                    }}
                />
                <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

                <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-9">
                        <p className="overline sr-fade-in flex items-center gap-3 mb-8">
                            <span className="lime-dot" />
                            About Stout Road
                        </p>
                        <h1
                            data-testid="about-headline"
                            className="font-heading text-white sr-fade-up text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight"
                        >
                            Built by a business partner with a strategic mindset,
                            <br />
                            <span className="italic font-light text-[#A3A8D8]">
                                for operators who lead with purpose
                            </span>
                            <span className="text-[#C6F432]">.</span>
                        </h1>
                        <div className="sr-fade-up delay-2 mt-8 max-w-2xl space-y-5 text-base md:text-lg leading-relaxed text-white/65 font-light">
                            <p>
                                At Stout Road, we know that finance done well is
                                more than numbers — it's a strategic advantage that
                                brings clarity to complex challenges. Clarity
                                empowers leaders to make confident, growth-driven
                                decisions that move the business forward.
                            </p>
                            <p className="text-white">
                                We bring that clarity and strategic insight to
                                every partnership.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Founder Story */}
            <section
                data-testid="founder-section"
                className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#050508]"
            >
                <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-10 md:gap-16 items-start">
                    {/* Portrait */}
                    <div className="col-span-12 md:col-span-5">
                        <div
                            data-testid="founder-portrait"
                            className="relative aspect-[4/5] overflow-hidden logo-frame"
                        >
                            <img
                                src={PORTRAIT_PLACEHOLDER}
                                alt="Kumen — Founder of Stout Road"
                                className="w-full h-full object-cover object-top"
                                style={{
                                    maskImage:
                                        "linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)",
                                    WebkitMaskImage:
                                        "linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)",
                                }}
                            />
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(91,58,120,0.18) 0%, transparent 35%, rgba(5,5,8,0.55) 100%)",
                                }}
                            />
                            <div className="absolute bottom-4 left-4 right-4">
                                <p className="overline text-white/80">
                                    Founder & Managing Partner
                                </p>
                                <p className="font-heading text-2xl text-white mt-1">
                                    Kumen R. Call
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Story */}
                    <div className="col-span-12 md:col-span-7">
                        <p className="overline flex items-center gap-3 mb-6">
                            <span className="lime-dot" />
                            Founder's Story
                        </p>
                        <h2
                            data-testid="founder-headline"
                            className="font-heading text-white text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight"
                        >
                            Clarity compounds.{" "}
                            <span className="italic text-[#A3A8D8]">
                                We bring it
                            </span>
                            .
                        </h2>

                        <div className="mt-10 space-y-6 text-white/65 text-base md:text-lg leading-relaxed font-light max-w-2xl">
                            <p>
                                Most companies don't stall because the team isn't
                                working hard enough. They stall because the numbers
                                aren't clear. They don't know what's actually
                                driving revenue. They can't see where cash is really
                                going. They lack the clean, actionable insight that
                                turns good intentions into confident decisions.
                            </p>
                            <p className="text-white text-xl md:text-2xl font-heading italic">
                                That's where we come in.
                            </p>
                            <p>
                                For more than twenty years I've led finance at
                                public companies, private businesses, and
                                private-equity-backed growth companies. I've built
                                the models that clarify and provide actionable
                                insight. I've sat on both sides of the table during
                                M&A diligence. I've stood up analytics
                                infrastructure that scaled teams from early revenue
                                to consistent profitability. I've lived and worked
                                with companies in 19 countries across North
                                America, Asia, and Europe — for US and British
                                publicly traded firms.
                            </p>
                            <p>
                                Every single time, the pattern was the same: once
                                the leadership team could finally see the business
                                clearly, everything accelerated.
                            </p>
                            <p>
                                I founded Stout Road to deliver that same clarity —
                                but in a smarter, more flexible way. Instead of a
                                full-time CFO hire that locks you into high
                                overhead, I work fractionally or on an interim
                                basis. You get the same board-ready financial
                                leadership, the same rigor, and the same strategic
                                partnership — without the full-time cost or
                                long-term commitment.
                            </p>

                            <div className="pt-2">
                                <p className="overline text-[#A3A8D8] mb-5">
                                    What that looks like in practice
                                </p>
                                <ul className="space-y-4">
                                    {[
                                        "The board deck that actually drives a decision, not just reports numbers",
                                        "Cash forecasts that make sense and show opportunities",
                                        "The monthly reporting package understandable to both finance and non-finance leaders",
                                    ].map((item) => (
                                        <li
                                            key={item}
                                            className="flex gap-4 items-start"
                                        >
                                            <span className="lime-dot mt-2.5 shrink-0" />
                                            <span className="text-white/75">
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <p>
                                We don't just clean up the books. We install the
                                financial clarity that compounds — month after
                                month, quarter after quarter — long after our
                                engagement ends.
                            </p>
                            <p>
                                If your company has reached the point where growth
                                feels harder than it should, or the numbers feel
                                fuzzy when they need to be crystal clear, let's
                                talk. Stout Road exists for exactly those moments.
                            </p>

                            <div className="pt-4 border-t border-[rgba(163,168,216,0.12)] space-y-1">
                                <p className="font-heading text-white text-2xl md:text-3xl leading-tight">
                                    We bring the clarity for the road ahead.
                                </p>
                                <p className="font-heading italic text-[#A3A8D8] text-xl md:text-2xl">
                                    The rest compounds from there.
                                </p>
                            </div>
                        </div>

                        {/* Highlights */}
                        <div
                            data-testid="founder-highlights"
                            className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(163,168,216,0.08)]"
                        >
                            {[
                                {
                                    k: "20+",
                                    v: "Years in finance",
                                    size: "big",
                                },
                                {
                                    k: "Public · Private · PE-Backed",
                                    v: "Operating experience",
                                    size: "text",
                                },
                                {
                                    k: "19 Countries",
                                    v: "NA · Asia · Europe",
                                    size: "text",
                                },
                            ].map((stat) => (
                                <div
                                    key={stat.v}
                                    className="bg-[#0A0710] p-6 md:p-8 flex flex-col justify-between min-h-[160px]"
                                >
                                    <p
                                        className={`font-heading text-[#C6F432] leading-[1.05] ${
                                            stat.size === "big"
                                                ? "text-4xl md:text-5xl"
                                                : "text-xl md:text-2xl"
                                        }`}
                                    >
                                        {stat.k}
                                    </p>
                                    <p className="overline text-white/50 mt-4">
                                        {stat.v}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section
                data-testid="values-section"
                className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#07050C] border-t border-[rgba(163,168,216,0.08)] overflow-hidden"
            >
                <div
                    aria-hidden="true"
                    className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(91,58,120,0.22) 0%, transparent 70%)",
                        filter: "blur(50px)",
                    }}
                />
                <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-4">
                        <p className="overline flex items-center gap-3">
                            <span className="lime-dot" />
                            What we believe
                        </p>
                        <h2 className="font-heading text-white text-3xl md:text-4xl lg:text-5xl leading-tight mt-6">
                            Three principles
                            <br />
                            <span className="italic text-[#A3A8D8]">
                                of action
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-8 md:pl-8 space-y-8">
                        {[
                            {
                                h: "Embedded, not advisory",
                                p: "We work as part of your team — held accountable to the same outcomes you are.",
                            },
                            {
                                h: "Compounding Impact",
                                p: "Every model, process, and output we create is built to compound. The clarity we install keeps delivering better decisions and stronger growth — long after the engagement ends.",
                            },
                            {
                                h: "Built to outlast us",
                                p: "We treat your business as our own. Models, processes, and reporting that hold up whether we're there or not — so the foundation compounds long after the engagement.",
                            },
                        ].map((v, i) => (
                            <div
                                key={v.h}
                                className="flex gap-6 pb-8 border-b border-[rgba(163,168,216,0.1)] last:border-b-0"
                            >
                                <div className="font-heading text-[#C6F432] text-2xl md:text-3xl w-12 shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div>
                                    <h3 className="font-heading text-white text-2xl md:text-3xl mb-2">
                                        {v.h}
                                    </h3>
                                    <p className="text-white/60 text-base leading-relaxed font-light max-w-2xl">
                                        {v.p}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section
                data-testid="about-cta"
                className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#050508] border-t border-[rgba(163,168,216,0.08)]"
            >
                <div className="max-w-[1100px] mx-auto text-center">
                    <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Have a road ahead{" "}
                        <span className="italic text-[#A3A8D8]">
                            worth mapping
                        </span>
                        ?
                    </h2>
                    <p className="mt-6 max-w-xl mx-auto text-white/60 text-base md:text-lg leading-relaxed font-light">
                        Let's talk about where you're headed and what the finance
                        function needs to look like to get you there.
                    </p>
                    <div className="mt-10 flex justify-center">
                        <button
                            data-testid="about-cta-button"
                            onClick={openCalendly}
                            className="btn-primary"
                        >
                            Book a 30-min Call
                            <ArrowUpRight size={14} />
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
